import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  assignEmployeeColors,
  checkInTime,
  chipLabel,
  computeActivityCoverage,
  computeActivityPresence,
  profileOutputsForDay,
  sortCoverageByStart,
  workloadFromDay,
  type ActivityOverride,
  type ActivityPresence,
  type ActivityQualification,
  type BoardActivity,
  type BoardDay,
  type DayPresence,
  type Employee,
  todayIsoParis,
} from '@lemontaigu/staff';
import {
  useBoard,
  useBoardPresence,
  useBoardRealtime,
  useRoster,
} from '@lemontaigu/staff/react';
import { coverageStatusLabel } from '@lemontaigu/reception/team/logic.js';
import { ChargeDay, useChargeConfig } from '@lemontaigu/reception/team/ChargeBoard.js';
import { ServiceTabs } from '@lemontaigu/reception/team/ServiceLists.js';
import { PresenceMatrix } from '@lemontaigu/reception/team/PresenceMatrix.js';
import { FrDateInput } from '@lemontaigu/reception/ui/FrDateInput.js';
import { supabase } from './supabase.js';
import { fmtDayLabel, shiftDay } from './logic.js';
import { STAFF_API_BASE } from './config.js';

// « Présence » — the day view (ADR-0011): declared work is an ACTIVITY, not a
// slot. The signed-in employee taps the activities they'll do (only the ones
// they're qualified for) or an « Absent·e » toggle; slots/the 3-square matrix
// are derived. Fed by self-taps (this component, direct RLS writes), the desk,
// and AI-parsed channel messages. « Activités du jour » (2026-07-22) drops the
// separate read-only Présence-matrix strip in favour of ONE not-to-scale
// activity Gantt (phone-width bars, no hour axis — reusing the reception
// Gantt's chip/status visual language via team.css): tapping a qualified
// activity's bar toggles the signed-in employee's own declaration on it
// (same self-tap RLS path as the top strip); a same-team colleague not yet
// covering can still be tap-added (POST /api/staff/tap-assign) — each
// covering chip carries its provenance (★ assigned, 📱/💬/⚡/« via » —
// self-tap, message, clock, or desk) so it's visible who declared whom.
// Removing MY OWN chip is always allowed regardless of source/status
// (migration 0049) — a stale ★ assignment or desk declaration I can't
// actually fulfil is worse for the team than an honest gap, so qualification
// only gates ADDING a new self-declaration, never retracting an existing one.

// Column lists and row mappers moved into the C3 adapters at ladder step 6.
// This file's DAY_COLUMNS had also drifted — it omitted `occupancy` and
// `diners`, which the shared list carries; both are optional in the mapper, so
// the view rendered the same either way and the drift was invisible. One list
// is the point.
//
// The tap WRITES below still name their tables; they move at step 7.

export function Presence() {
  const board = useBoard();
  const roster = useRoster();
  const boardPresence = useBoardPresence();
  const boardRealtime = useBoardRealtime();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [myEmployeeId, setMyEmployeeId] = useState<string | null>(null);
  const [day, setDay] = useState(() => todayIsoParis());
  const [presences, setPresences] = useState<ActivityPresence[] | null>(null);
  const [absences, setAbsences] = useState<string[]>([]);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [qualifications, setQualifications] = useState<ActivityQualification[]>([]);
  const [boardDay, setBoardDay] = useState<BoardDay | null>(null);
  const [overrides, setOverrides] = useState<ActivityOverride[]>([]);
  const [err, setErr] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [emps, myId, acts, quals] = await Promise.all([
          roster.current(),
          roster.myEmployeeId(),
          board.activities(),
          board.qualifications(),
        ]);
        setEmployees(emps);
        setMyEmployeeId(myId);
        // Untimed activities (« sans horaire », e.g. Téléphone) sign up the same
        // way as timed ones — self-tap, qualification-gated (2026-07-23). They
        // are excluded from the timed Gantt-like list below, not from the roster.
        setActivities(acts);
        setQualifications(quals);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [board, roster]);

  const load = useCallback(
    async (key: string) => {
      setPresences(null);
      try {
        const [p, ab, d, o] = await Promise.all([
          board.presence(key, key),
          board.absences(key, key),
          board.days(key, key),
          board.overrides(key, key),
        ]);
        setPresences(p);
        setAbsences(ab.map((r) => r.employeeId));
        setBoardDay(d[0] ?? null);
        setOverrides(o);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board],
  );

  useEffect(() => {
    void load(day);
  }, [day, load]);

  useEffect(() => {
    // This view has one reload path, so it ignores WHICH kind of change it was.
    return boardRealtime.watchBoard(() => void load(day));
  }, [day, load, boardRealtime]);

  const today = todayIsoParis();
  const employeesById = new Map(employees.map((e) => [e.id, e]));
  const myTeam = myEmployeeId ? (employeesById.get(myEmployeeId)?.team ?? null) : null;

  // The activities I may declare myself on — qualified + active, in sort order.
  const myActivities = useMemo(() => {
    if (!myEmployeeId) return [];
    const qualified = new Set(qualifications.filter((q) => q.employeeId === myEmployeeId).map((q) => q.activityId));
    return activities.filter((a) => a.active && qualified.has(a.id));
  }, [activities, qualifications, myEmployeeId]);
  const myActivityIds = new Set(
    (presences ?? []).filter((p) => p.employeeId === myEmployeeId).map((p) => p.activityId),
  );
  const iAmAbsent = myEmployeeId ? absences.includes(myEmployeeId) : false;

  // Tap an activity: toggle my own declared row (source='tap'). Declaring lifts
  // a stale absence — in ONE transaction now (step 7 / migration 0055; this was
  // two unchecked writes, and the absence delete's error was discarded). RLS
  // still enforces self-only + qualification.
  const declareSelfActivity = async (activityId: number) => {
    if (!myEmployeeId || presences === null) return;
    setBusyKey(`a${activityId}`);
    setErr('');
    try {
      if (myActivityIds.has(activityId)) {
        await boardPresence.withdrawPresence(activityId, myEmployeeId, day);
      } else {
        await boardPresence.declarePresence({
          activityId,
          employeeId: myEmployeeId,
          date: day,
          status: 'declared',
          source: 'tap',
          declaredBy: myEmployeeId,
        });
      }
      void load(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  // « Absent·e » toggle — a day-level flag, exclusive with presence. Setting it
  // clears my rows for the day in the same transaction, and fails outright if
  // any of them could not be cleared rather than leaving me both present and
  // absent (step 7 / migration 0055).
  const toggleAbsent = async () => {
    if (!myEmployeeId) return;
    setBusyKey('absent');
    setErr('');
    try {
      if (iAmAbsent) await boardPresence.clearAbsence(myEmployeeId, day);
      else
        await boardPresence.markAbsent({
          employeeId: myEmployeeId,
          date: day,
          source: 'tap',
          declaredBy: myEmployeeId,
        });
      void load(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  // ── Badges (ADR-0015) ────────────────────────────────────────────────────
  // The registry and the staffing profiles are CONFIGURATION: loaded once, and
  // never allowed to fail the day. Without them a badge falls back to the raw
  // demand number rather than the page failing to render. The load + build was
  // the fourth copy of that block; it moved into `useChargeConfig` when the
  // Charge board arrived here (2026-09-05) and both now read the same registry.
  const { registry, specs } = useChargeConfig();
  const metricCtx = useMemo(
    () => ({
      day: boardDay,
      profileOutputs: boardDay
        ? profileOutputsForDay({
            specs,
            day: boardDay,
            workload: workloadFromDay(boardDay),
            readyBy: checkInTime(boardDay.arrivals),
          })
        : undefined,
    }),
    [specs, boardDay],
  );

  const coverage = useMemo(
    () =>
      computeActivityCoverage({
        date: day,
        activities,
        overrides,
        day: boardDay,
        presences: presences ?? [],
        currentEmployeeIds: employees.map((e) => e.id),
        absentEmployeeIds: absences,
        metrics: registry,
        metricContext: metricCtx,
      }),
    [day, activities, overrides, boardDay, presences, employees, absences, registry, metricCtx],
  );

  // Untimed activities have no place on the hour-axis-shaped mgantt bars (their
  // window is an unused 00:00 placeholder) — split them into their own list,
  // same split reception's DayView makes for the real Gantt.
  const sortedCoverage = useMemo(
    () => sortCoverageByStart(coverage.activities.filter((c) => !c.activity.untimed)),
    [coverage.activities],
  );
  const untimedCoverage = useMemo(
    () => coverage.activities.filter((c) => c.activity.untimed),
    [coverage.activities],
  );
  // The « Présence » matrix, one single row for this day (operator request
  // 2026-08-04): the same table the Semaine/Mois horizons render — who's
  // matin/après-midi/soir vs full-red Absent·e — so the day view answers « qui
  // est en repos aujourd'hui ? » at a glance, above the self-declare strip.
  // Untimed activities carry a 00:00 placeholder window and would read as a
  // phantom morning third, so they're excluded exactly as the week matrix does.
  const timedActivities = useMemo(() => activities.filter((a) => !a.untimed), [activities]);
  const presenceByDay = useMemo(() => {
    const map = new Map<string, Map<string, DayPresence>>();
    map.set(
      day,
      computeActivityPresence({
        date: day,
        activities: timedActivities,
        overrides,
        presences: presences ?? [],
        absentEmployeeIds: absences,
        currentEmployeeIds: employees.map((e) => e.id),
      }),
    );
    return map;
  }, [day, timedActivities, overrides, presences, absences, employees]);

  const colors = useMemo(() => assignEmployeeColors(employees.map((e) => e.id)), [employees]);
  const qualifiedIds = useMemo(
    () => new Set(qualifications.filter((q) => q.employeeId === myEmployeeId).map((q) => q.activityId)),
    [qualifications, myEmployeeId],
  );

  // Who declared whom, for the covering chip's provenance marker — the same
  // reading as reception's DayView.provenanceFor (assigned rows carry no
  // marker of their own, the ★ ring already says it).
  const provenanceFor = (activityId: number, employeeId: string): string => {
    const p = (presences ?? []).find((x) => x.activityId === activityId && x.employeeId === employeeId);
    if (!p || p.status === 'assigned') return '';
    if (p.source === 'tap') return '📱';
    if (p.source === 'message') return '💬';
    if (p.source === 'clock') return '⚡';
    if (p.source === 'desk' && p.declaredBy) {
      const via = employeesById.get(p.declaredBy);
      return via ? `via ${via.firstName}` : 'via …';
    }
    return '';
  };

  const [tapBusy, setTapBusy] = useState<string | null>(null);
  const [tapErr, setTapErr] = useState('');

  const tapDeclare = async (activityId: number, targetEmployeeId: string) => {
    const key = `${activityId}:${targetEmployeeId}`;
    setTapBusy(key);
    setTapErr('');
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('session expirée');
      const res = await fetch(`${STAFF_API_BASE}/api/staff/tap-assign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ activityId, employeeId: targetEmployeeId, date: day }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? `déclaration impossible (${res.status})`);
      }
      void load(day);
    } catch (e) {
      setTapErr(e instanceof Error ? e.message : 'déclaration impossible');
    } finally {
      setTapBusy(null);
    }
  };

  return (
    <div className="staff">
      <div className="staff-daybar">
        <button type="button" onClick={() => setDay(shiftDay(day, -1))} aria-label="Jour précédent">
          ‹
        </button>
        <FrDateInput className="pt-datepicker" value={day} onChange={(v) => v && setDay(v)} />
        <button type="button" onClick={() => setDay(shiftDay(day, 1))} aria-label="Jour suivant">
          ›
        </button>
        <span className="staff-daylabel">{fmtDayLabel(day)}</span>
        {day !== today && (
          <button type="button" className="staff-today" onClick={() => setDay(today)}>
            Aujourd’hui
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {/* « Charge » — the same day the desk reads, read-only (operator,
          2026-09-05). It sits ABOVE the presence matrix and the self-declare
          strip because it is the answer to "what kind of day is this?", which
          is the question every other block on this screen is a response to:
          you decide whether to tap yourself onto Ménage after seeing 22
          départs, not before. Compact by default — this app is a phone. */}
      <ChargeDay day={boardDay} registry={registry} specs={specs} compact />

      {/* Who is eating, under how many are (0147). Tabs rather than two lists:
          a phone shows one thing at a time, and the two services are consulted
          at different moments anyway. No « Nom » column — it is the widest and
          the least useful here, since a cook checking « how many at 0205 »
          identifies the party by its room. */}
      <ServiceTabs day={day} />

      {presences !== null && (
        <PresenceMatrix
          employees={employees}
          days={[day]}
          presenceByDay={presenceByDay}
          today={today}
          title="Présence du jour"
        />
      )}

      {myEmployeeId && (
        <div className="presence-self">
          <span className="presence-self-label">Mes activités ce jour :</span>
          <div className="presence-self-buttons">
            {myActivities.map((a) => (
              <button
                key={a.id}
                type="button"
                className={myActivityIds.has(a.id) ? 'is-active' : ''}
                disabled={busyKey !== null || iAmAbsent}
                onClick={() => void declareSelfActivity(a.id)}
              >
                {busyKey === `a${a.id}` ? '…' : a.label}
              </button>
            ))}
            <button
              type="button"
              className={`presence-self-absent${iAmAbsent ? ' is-active' : ''}`}
              disabled={busyKey !== null}
              onClick={() => void toggleAbsent()}
            >
              {busyKey === 'absent' ? '…' : 'Absent·e'}
            </button>
          </div>
        </div>
      )}

      {presences === null && <p className="staff-empty">Chargement…</p>}

      {tapErr && <p className="auth-err">Erreur : {tapErr}</p>}

      {presences !== null && sortedCoverage.length > 0 && (
        <div className="presence-coverage">
          <h3 className="presence-coverage-title">
            Activités du jour
            {!coverage.demandEntered && <span className="presence-coverage-hint">(charge non saisie)</span>}
          </h3>
          <div className="mgantt">
            {sortedCoverage.map((c) => {
              const covering =
                c.status === 'covered'
                  ? [
                      ...c.assigned.map((id) => ({ id, assigned: true })),
                      ...c.coveredBy.map((id) => ({ id, assigned: false })),
                    ]
                  : [];
              const coveringIds = new Set(covering.map((p) => p.id));
              const qualified = qualifiedIds.has(c.activity.id);
              const amOn = myEmployeeId !== null && coveringIds.has(myEmployeeId);
              // Removing my OWN row is always allowed — even an admin ★
              // assignment or a desk/message/clock declaration I didn't tap
              // myself (0049): a stale "I'm on it" the team can't actually
              // count on is worse than an accurate gap. Only ADDING a new
              // self-declaration still requires qualification + not-absent.
              const canToggle = amOn || (qualified && !iAmAbsent);
              const helpers = myTeam
                ? employees.filter(
                    (e) =>
                      e.team === myTeam &&
                      e.id !== myEmployeeId &&
                      !coveringIds.has(e.id) &&
                      qualifications.some((q) => q.activityId === c.activity.id && q.employeeId === e.id),
                  )
                : [];
              return (
                <div key={c.activity.id} className={`mgantt-row team-${c.activity.team ?? 'none'}`}>
                  <button
                    type="button"
                    className={`mgantt-bar is-${c.status}${amOn ? ' is-mine' : ''}`}
                    disabled={!canToggle || busyKey !== null}
                    aria-pressed={amOn}
                    title={
                      amOn
                        ? `Retirer ma présence — ${c.activity.label} (${coverageStatusLabel(c.status)})`
                        : qualified
                          ? `Déclarer ma présence — ${c.activity.label} (${coverageStatusLabel(c.status)})`
                          : `${c.activity.label} — pas qualifié·e pour cette activité (${coverageStatusLabel(c.status)})`
                    }
                    onClick={() => void declareSelfActivity(c.activity.id)}
                  >
                    <span className="mgantt-label">
                      {busyKey === `a${c.activity.id}` ? '…' : c.activity.label}
                    </span>
                    <span className="mgantt-window">
                      {c.windowStart.slice(0, 5)}–{c.windowEnd.slice(0, 5)}
                    </span>
                    <span className="mgantt-chips">
                      {covering.map(({ id, assigned }) => {
                        const e = employeesById.get(id);
                        const label = e ? chipLabel(e) : '?';
                        const provenance = provenanceFor(c.activity.id, id);
                        return (
                          <span
                            key={id}
                            className={`gantt-chip${assigned ? ' is-assigned' : ''}${id === myEmployeeId ? ' is-me' : ''}`}
                            style={{ background: colors.get(id) }}
                          >
                            {assigned && <span className="gantt-chip-star">★</span>}
                            {label}
                            {provenance && <span className="mgantt-chip-provenance">{provenance}</span>}
                          </span>
                        );
                      })}
                    </span>
                    {c.status === 'gap' && <span className="gantt-flag">⚠ à pourvoir</span>}
                    {c.status === 'waived' && <span className="gantt-muted">dispensé</span>}
                    {c.status === 'noDemand' && <span className="gantt-muted">pas de besoins</span>}
                    {c.badge ? (
                      <span className="gantt-demand" title={c.badge.labelFr}>
                        {c.badge.text}
                      </span>
                    ) : (
                      c.demand > 1 && <span className="gantt-demand">{c.demand}</span>
                    )}
                  </button>
                  {helpers.length > 0 && (
                    <div className="mgantt-helpers">
                      {helpers.map((e) => {
                        const key = `${c.activity.id}:${e.id}`;
                        return (
                          <button
                            key={e.id}
                            type="button"
                            className="presence-tap-chip"
                            disabled={tapBusy !== null}
                            title={`Marquer ${e.firstName} présent·e pour ${c.activity.label}`}
                            onClick={() => void tapDeclare(c.activity.id, e.id)}
                          >
                            {tapBusy === key ? '…' : `+ ${chipLabel(e)}`}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {presences !== null && untimedCoverage.length > 0 && (
        <div className="presence-coverage">
          <h3 className="presence-coverage-title">Activités sans horaire</h3>
          {/* Sign-up activities (« Téléphone ») — no window, so no bar; a chip
              row per activity. Toggling reuses the same self-tap declare as
              « Mes activités » above (source='tap', qualification-gated). */}
          <ul className="presence-untimed">
            {untimedCoverage.map((c) => {
              const covering = [
                ...c.assigned.map((id) => ({ id, assigned: true })),
                ...c.coveredBy.map((id) => ({ id, assigned: false })),
              ];
              const coveringIds = new Set(covering.map((p) => p.id));
              const qualified = qualifiedIds.has(c.activity.id);
              const amOn = myEmployeeId !== null && coveringIds.has(myEmployeeId);
              const canToggle = amOn || (qualified && !iAmAbsent);
              return (
                <li key={c.activity.id} className={`presence-untimed-row team-${c.activity.team ?? 'none'}`}>
                  <span className="presence-untimed-label">{c.activity.label}</span>
                  <span className="mgantt-chips">
                    {covering.map(({ id, assigned }) => {
                      const e = employeesById.get(id);
                      const label = e ? chipLabel(e) : '?';
                      const provenance = provenanceFor(c.activity.id, id);
                      return (
                        <span
                          key={id}
                          className={`gantt-chip${assigned ? ' is-assigned' : ''}${id === myEmployeeId ? ' is-me' : ''}`}
                          style={{ background: colors.get(id) }}
                        >
                          {assigned && <span className="gantt-chip-star">★</span>}
                          {label}
                          {provenance && <span className="mgantt-chip-provenance">{provenance}</span>}
                        </span>
                      );
                    })}
                  </span>
                  <button
                    type="button"
                    className={`presence-untimed-toggle${amOn ? ' is-active' : ''}`}
                    disabled={!canToggle || busyKey !== null}
                    aria-pressed={amOn}
                    title={
                      amOn
                        ? `Retirer ma présence — ${c.activity.label}`
                        : qualified
                          ? `M’inscrire — ${c.activity.label}`
                          : `Pas qualifié·e pour ${c.activity.label}`
                    }
                    onClick={() => void declareSelfActivity(c.activity.id)}
                  >
                    {busyKey === `a${c.activity.id}` ? '…' : amOn ? 'Retirer' : 'M’inscrire'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
